// src/components/RPA/buttons/UploadBankStatementsButton.js
import React, { useState, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';

/**
 * UploadBankStatementsButton
 * 
 * Allows uploading multiple bank statement files (CSV, Excel, or PDF)
 * for cash verification procedures in the Financial Statement Audit workflow.
 * 
 * Features:
 * - Multi-file upload (up to 10 bank accounts)
 * - Supports CSV, XLS, XLSX, PDF formats
 * - Shows upload progress
 * - Displays summary of parsed data
 * - Real-time feedback on flagged transactions
 */
const UploadBankStatementsButton = ({ docId, onDone, disabledHint }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadSummary, setUploadSummary] = useState(null);
  const fileInputRef = useRef(null);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Validate file count
    if (files.length > 10) {
      setError('Maximum 10 bank statement files allowed');
      return;
    }

    // Validate file types
    const validExtensions = ['.csv', '.xls', '.xlsx', '.pdf'];
    const invalidFiles = Array.from(files).filter(file => {
      const ext = file.name.toLowerCase().match(/\.[^.]+$/)?.[0];
      return !validExtensions.includes(ext);
    });

    if (invalidFiles.length > 0) {
      setError(`Invalid file types: ${invalidFiles.map(f => f.name).join(', ')}. Only CSV, Excel, and PDF files are supported.`);
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');
    setUploadSummary(null);

    try {
      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append('bankStatements', file);
      });

      console.log('[BANK_STATEMENTS] Uploading', files.length, 'files for doc', docId);

      const res = await axios.post(
        `${API_URL}/admin/rpa/docs/${docId}/bank-statements/upload`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
          timeout: 120000 // 2 minute timeout for large files
        }
      );

      console.log('[BANK_STATEMENTS] Upload response:', res.data);

      if (res.data.success) {
        const summary = res.data.summary || {};
        setSuccess(
          `✅ ${summary.accountCount || files.length} bank statement(s) uploaded successfully! ` +
          `${summary.totalTransactions || 0} transactions analyzed, ` +
          `${summary.totalUnusualFlags || 0} unusual items flagged.`
        );
        setUploadSummary(summary);

        // Clear file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        // Call onDone callback after short delay to show success message
        if (onDone) {
          setTimeout(() => {
            onDone();
          }, 2000);
        }
      } else {
        setError(res.data.message || 'Upload failed');
      }
    } catch (err) {
      console.error('[BANK_STATEMENTS] Upload error:', err);
      const errMsg = err.response?.data?.message || err.message || 'Upload failed';
      setError(`Failed to upload: ${errMsg}`);
    } finally {
      setUploading(false);
    }
  };

  const disabled = uploading || !!disabledHint;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <label
          style={{
            background: disabled ? '#e5e7eb' : '#10b981',
            color: disabled ? '#9ca3af' : 'white',
            border: 'none',
            padding: '6px 12px',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'inline-block',
            transition: 'all 0.2s'
          }}
        >
          {uploading ? '⏳ Uploading...' : '🏦 Upload Bank Statements'}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".csv,.xls,.xlsx,.pdf"
            onChange={handleUpload}
            disabled={disabled}
            style={{ display: 'none' }}
          />
        </label>

        {disabledHint && (
          <span
            style={{
              fontSize: 11,
              color: '#f59e0b',
              fontStyle: 'italic'
            }}
          >
            ⚠️ {disabledHint}
          </span>
        )}
      </div>

      {uploading && (
        <div
          style={{
            padding: 8,
            background: '#dbeafe',
            border: '1px solid #3b82f6',
            borderRadius: 6,
            fontSize: 12,
            color: '#1e40af'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 16,
                height: 16,
                border: '2px solid #3b82f6',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}
            />
            <span>Uploading and parsing bank statements...</span>
          </div>
        </div>
      )}

      {error && (
        <div
          style={{
            padding: 10,
            background: '#fef2f2',
            border: '1px solid #fca5a5',
            borderRadius: 6,
            fontSize: 12,
            color: '#991b1b'
          }}
        >
          ❌ {error}
        </div>
      )}

      {success && (
        <div
          style={{
            padding: 10,
            background: '#f0fdf4',
            border: '1px solid #86efac',
            borderRadius: 6,
            fontSize: 12,
            color: '#15803d'
          }}
        >
          {success}
        </div>
      )}

      {uploadSummary && uploadSummary.accounts && uploadSummary.accounts.length > 0 && (
        <div
          style={{
            padding: 12,
            background: '#f0f9ff',
            border: '1px solid #0ea5e9',
            borderRadius: 6,
            fontSize: 12,
            marginTop: 4
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: 8, color: '#0369a1' }}>
            📊 Upload Summary
          </div>
          {uploadSummary.accounts.map((account, idx) => (
            <div
              key={idx}
              style={{
                padding: 8,
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: 4,
                marginBottom: 6
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 'bold', fontSize: 13 }}>
                    {account.accountName} ({account.accountNumber})
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                    {account.bankName}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 14, fontWeight: 'bold', color: '#059669' }}>
                    ${account.closingBalance.toLocaleString()}
                  </div>
                  <div style={{ fontSize: 10, color: '#6b7280' }}>
                    {account.transactionCount} transactions
                  </div>
                </div>
              </div>
              {account.unusualTransactions > 0 && (
                <div
                  style={{
                    marginTop: 6,
                    padding: 6,
                    background: '#fef3c7',
                    border: '1px solid #f59e0b',
                    borderRadius: 4,
                    fontSize: 11,
                    color: '#92400e'
                  }}
                >
                  ⚠️ {account.unusualTransactions} unusual transaction(s) flagged for review
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div
        style={{
          fontSize: 10,
          color: '#6b7280',
          marginTop: 4,
          lineHeight: 1.4
        }}
      >
        💡 <strong>Tip:</strong> You can upload up to 10 bank statement files at once (one per account).
        Supported formats: CSV, Excel (.xls, .xlsx), PDF. The system will automatically parse transactions
        and flag unusual activity for your review.
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default UploadBankStatementsButton;